import { useEffect, useRef, useState, useCallback } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/lib/i18n";

export default function ChatPanel({ bidId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(() => api.get(`/connections/${bidId}/messages`).then((r) => setMsgs(r.data)).catch(() => {}), [bidId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/connections/${bidId}/messages`, { text });
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) { toast.error(errMsg(err)); }
    setSending(false);
  };

  return (
    <div className="hf-card flex flex-col h-[520px]" data-testid="chat-panel">
      <div className="px-5 py-3 border-b font-mono text-xs uppercase tracking-wider text-slate-500">{t("Load planning chat")}</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/60">
        {msgs.length === 0 && <p className="text-sm text-slate-500 text-center mt-10">{t("Say hello and start planning the load.")}</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="chat-message">
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}>
                <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-[10px] font-mono mt-1 ${mine ? "text-slate-400" : "text-slate-400"}`}>{mine ? t("You") : m.sender_name} · {fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("Type a message…")} className="hf-input" data-testid="chat-input" />
        <button disabled={sending} className="hf-btn-primary px-4" data-testid="btn-send-freight-chat"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}
